/**
 * The item colour, as the hex field and the picker each need it.
 *
 * The two controls edit one value but disagree about what is acceptable: the text field has to
 * tolerate a half-typed `#20`, while `<input type="color">` only ever reports and accepts a
 * complete `#rrggbb` and silently falls back to black when handed anything else.
 *
 * @module Pages/BUs/WhatsNew
 */

const FULL_HEX = /^#[0-9a-fA-F]{6}$/;
const SHORT_HEX = /^#[0-9a-fA-F]{3}$/;

/** The colour used when the field holds nothing usable yet. */
export const DEFAULT_ITEM_COLOR = '#0033FF';

/** Whether the value is a colour the picker can show. */
export function isHexColor(value: string): boolean {
  return FULL_HEX.test(value.trim());
}

/**
 * What to hand `<input type="color">`.
 *
 * Expands the three-digit form, because the picker does not understand it and would jump to black
 * mid-typing. Anything still unusable falls back instead of resetting the person's colour.
 */
export function toPickerValue(value: string, fallback: string = DEFAULT_ITEM_COLOR): string {
  const trimmed = value.trim();

  if (FULL_HEX.test(trimmed)) return trimmed.toUpperCase();

  if (SHORT_HEX.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return fallback;
}

/**
 * Cleans up what was typed or pasted in the hex field.
 *
 * Keeps it permissive: a lone `#` or a partial value is returned as-is so the field does not fight
 * the person mid-typing. It only adds the missing `#` and drops characters that cannot be hex.
 */
export function normalizeHexInput(value: string): string {
  const withoutHash = value.trim().replace(/^#+/, '');
  const hex = withoutHash.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);

  return `#${hex.toUpperCase()}`;
}
